<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  props: {
    modelValue: Boolean,
    idDahanProfesi: {
      type: [String, Number],
      required: true,
    },
    kodeProfesi: {
      type: [String, Number],
      required: false,
    },
    title: {
      type: String,
      default: "Sisa Asesmen",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      // list state
      loadingList: false,
      listProfesi: [],
      // detail state when a profesi is clicked
      loadingDetails: false,
      selectedProfesi: null,
      details: null,
      error: null,
    };
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.fetchListProfesi();
      } else {
        this.resetState();
      }
    },
  },
  methods: {
    resetState() {
      this.loadingList = false;
      this.listProfesi = [];
      this.loadingDetails = false;
      this.selectedProfesi = null;
      this.details = null;
      this.error = null;
    },

    async fetchListProfesi() {
      if (!this.idDahanProfesi) {
        this.error = "Dahan Profesi tidak tersedia.";
        return;
      }

      this.loadingList = true;
      this.error = null;
      this.listProfesi = [];

      const config = {
        method: "get",
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "peserta/jadwal-peserta/get-list-profesi-from-dahan",
        params: {
          id_dahan_profesi: this.idDahanProfesi,
          kode_profesi: this.kodeProfesi,
        },
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + localStorage.access_token_peserta,
        },
      };

      try {
        const response = await axios(config);
        const responseData = response.data;
        const payload = responseData?.data ?? responseData;

        // normalize list
        if (Array.isArray(payload)) {
          this.listProfesi = payload;
        } else if (Array.isArray(payload?.list)) {
          this.listProfesi = payload.list;
        } else if (Array.isArray(payload?.data)) {
          this.listProfesi = payload.data;
        } else {
          // try to extract array-ish
          this.listProfesi = payload || [];
        }
      } catch (error) {
        this.error =
          error?.response?.data?.data?.message || error?.message ||
          "Gagal memuat daftar profesi.";
        console.error("fetchListProfesi error", error);
        Swal.fire({
          icon: "error",
          title: "Terjadi kesalahan",
          text: this.error,
        });
      } finally {
        this.loadingList = false;
      }
    },

    async onSelectProfesi(item) {
      // set selected and fetch details
      this.selectedProfesi = item;
      this.details = null;
      await this.fetchProfesiDetails(item);
    },

    async fetchProfesiDetails(item) {
      this.loadingDetails = true;
      this.details = null;
      this.error = null;

      const params = {
        id_mprofesi: item.id_mprofesi ?? item.id_mprofesi,
        id_dahan_profesi: this.idDahanProfesi,
      };

      const config = {
        method: "get",
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "peserta/jadwal-peserta/sisa-kompetensi-profesi",
        params: params,
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + localStorage.access_token_peserta,
        },
      };

      try {
        const response = await axios(config);
        const responseData = response.data;
        this.details = responseData?.data ?? responseData;
      } catch (error) {
        this.error =
          error?.response?.data?.data?.message || error?.message ||
          "Gagal memuat detail profesi.";
        console.error("fetchProfesiDetails error", error);
        Swal.fire({ icon: "error", title: "Terjadi kesalahan", text: this.error });
      } finally {
        this.loadingDetails = false;
      }
    },

    closeModal() {
      this.resetState();
      this.$emit("update:modelValue", false);
    },
  },
  computed: {
    hasList() {
      return Array.isArray(this.listProfesi) && this.listProfesi.length > 0;
    },
  },
};
</script>

<template>
  <b-modal
    :model-value="modelValue"
    @hide="closeModal"
    centered
    hide-footer
    :title="title"
    dialog-class="modal-lg"
  >
    <div class="p-3">
      <div class="row">
        <!-- Left: list of profesi -->
        <div class="col-md-5">
          <div v-if="loadingList" class="text-center py-4">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <div class="mt-3">Memuat daftar profesi...</div>
          </div>

          <div v-else-if="error && !hasList" class="alert alert-danger">
            {{ error }}
          </div>

          <div v-else-if="hasList">
            <ul class="list-group">
              <li
                v-for="item in listProfesi"
                :key="item.id_mprofesi || item.id_mprofesi"
                class="list-group-item list-group-item-action"
                :class="{ active: selectedProfesi && selectedProfesi.id_mprofesi === item.id_mprofesi }"
                @click="onSelectProfesi(item)"
                style="cursor: pointer"
              >
                <div class="fw-bold">{{ item.kode_profesi }} - {{ item.nama_profesi }}</div>
                <div class="text-muted small">Status: {{ item.status || '-' }}</div>
              </li>
            </ul>
          </div>

          <div v-else class="text-center text-muted py-4">Tidak ada profesi.</div>
        </div>

        <!-- Right: details for selected profesi -->
        <div class="col-md-7">
          <div v-if="loadingDetails" class="text-center py-4">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <div class="mt-3">Memuat detail...</div>
          </div>

          <div v-else-if="selectedProfesi && details">
            <h5>{{ selectedProfesi.kode_profesi }} - {{ selectedProfesi.nama_profesi }}</h5>
            <div class="mb-3 text-muted">ID Profesi: {{ selectedProfesi.id_mprofesi }}</div>

            <div v-if="details.list_kompetensi && Array.isArray(details.list_kompetensi)">
              <div class="mb-2"><strong>Daftar Kompetensi</strong></div>
              <ul class="list-group">
                <li v-for="k in details.list_kompetensi" :key="k.idkompetensi" class="list-group-item">
                  <div class="fw-bold">{{ k.nama_kompetensi }}</div>
                  <div class="small text-muted">Level: {{ k.level_kompetensi_idlevel_kompetensi }}</div>
                </li>
              </ul>
            </div>

            <div v-else>
              <pre>{{ JSON.stringify(details, null, 2) }}</pre>
            </div>
          </div>

          <div v-else class="text-center text-muted py-4">Pilih profesi untuk melihat detail.</div>
        </div>
      </div>

      <div class="text-end mt-4">
        <button type="button" class="btn btn-secondary" @click="closeModal">Tutup</button>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
